import React, { useEffect, useState, useMemo } from "react";
import { SpecialItem, ExplosionMark } from "../../types";
import { EXPLOSION_MARK_DURATION } from "../../constants/gameConfig";
import { classNames, getDirectionClass } from "../../utils";

interface Props {
  isPlayer: boolean;
  isWolf: boolean;
  isTree: boolean;
  isFlower: boolean;
  isOverlap: boolean;
  isGrannyHouse: boolean;
  playerEnteredHouse: boolean;
  playerDirection?: string;
  wolfDirection?: string;
  gameOver?: boolean;
  wolfWon?: boolean;
  specialItem?: SpecialItem;
  isInExplosion?: boolean;
  explosionMark?: ExplosionMark;
  showStunTimer?: boolean;
  stunEndTime?: number | null;
  tooltipMessage?: string;
  showTooltip?: boolean;
  playerInvisible?: boolean;
  wolfConfused?: boolean;
}

const Tile: React.FC<Props> = ({
  isPlayer,
  isWolf,
  isTree,
  isFlower,
  isOverlap,
  isGrannyHouse,
  playerEnteredHouse,
  playerDirection,
  wolfDirection,
  gameOver,
  wolfWon,
  specialItem,
  isInExplosion,
  explosionMark,
  showStunTimer,
  stunEndTime,
  tooltipMessage,
  showTooltip = false,
  playerInvisible = false,
  wolfConfused = false,
}) => {
  const [scaleClass, setScaleClass] = useState("");
  const [stunTimeRemaining, setStunTimeRemaining] = useState<number>(0);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    if (isTree) {
      const randomNumber = Math.floor(Math.random() * 10) + 1;
      setScaleClass(`scale${randomNumber}`);
    }
  }, [isTree]);

  // display countdown timer on stunned wolf
  useEffect(() => {
    if (showStunTimer && stunEndTime) {
      const updateTimer = () => {
        const remaining = Math.max(0, Math.ceil((stunEndTime - Date.now()) / 1000));
        setStunTimeRemaining(remaining);
      };

      updateTimer();
      const interval = setInterval(updateTimer, 100);
      return () => clearInterval(interval);
    } else {
      setStunTimeRemaining(0);
    }
  }, [showStunTimer, stunEndTime]);

  // fade out explosion marks before they disappear
  useEffect(() => {
    if (!explosionMark) {
      setIsFading(false);
      return;
    }

    const checkFade = () => {
      const now = Date.now();
      const age = now - explosionMark.createdAt;
      const fadeStartTime = EXPLOSION_MARK_DURATION - 500;

      if (age >= fadeStartTime) {
        setIsFading(true);
      } else {
        setIsFading(false);
      }
    };

    checkFade();
    const interval = setInterval(checkFade, 50);
    return () => clearInterval(interval);
  }, [explosionMark]);

  // hide player sprite when wolf catches them
  const shouldShowPlayer = isPlayer && !(gameOver && (wolfWon || isOverlap));

  const className = useMemo(
    () =>
      classNames(
        "tile",
        shouldShowPlayer && "player",
        shouldShowPlayer && getDirectionClass("player", playerDirection),
        isWolf && "wolf",
        isWolf && getDirectionClass("wolf", wolfDirection),
        isTree && "tree",
        isTree && scaleClass,
        isFlower && "flower",
        isOverlap && isWolf && "wolf-overlap",
        isGrannyHouse && "granny-house",
        isGrannyHouse && showTooltip && "tooltip",
        playerEnteredHouse && isPlayer && isGrannyHouse && "player-in-house",
        specialItem && "special-item",
        isInExplosion && "explosion",
        explosionMark && "explosion-mark",
        showStunTimer && "wolf-stunned",
        playerInvisible && isPlayer && "player-invisible",
        wolfConfused && isWolf && "wolf-confused"
      ),
    [
      shouldShowPlayer,
      playerDirection,
      isWolf,
      wolfDirection,
      isTree,
      scaleClass,
      isFlower,
      isOverlap,
      isGrannyHouse,
      showTooltip,
      playerEnteredHouse,
      isPlayer,
      specialItem,
      isInExplosion,
      explosionMark,
      showStunTimer,
      playerInvisible,
      wolfConfused,
    ]
  );

  return (
    <div className={className} data-tooltip={showTooltip && tooltipMessage ? tooltipMessage : undefined}>
      {specialItem && (
        <div className={`special-item-icon ${specialItem.type}`}>
          {specialItem.type === "bomb" && "💣"}
          {specialItem.type === "cloak" && "🧥"}
        </div>
      )}
      {isInExplosion && <div className="explosion-effect" />}
      {explosionMark && (
        <div className={`explosion-mark-effect ${isFading ? "fading" : ""}`} />
      )}
      {showStunTimer && stunTimeRemaining > 0 && (
        <div className="stun-timer">
          <div className="stun-timer-text">{stunTimeRemaining}</div>
        </div>
      )}
    </div>
  );
};

export default Tile;
